import { CommonModule } from '@angular/common';
import { Component, EventEmitter, Input, OnInit, Output } from '@angular/core';
import { FormControl, Validators } from '@angular/forms';
import { PersonItem, PersonSearchOptions } from '@models/custom-fields.model';
import { ExistingAssetDetails, ShoppingCartData } from '@models/shopping-cart.model';
import { NgbTypeaheadSelectItemEvent } from '@ng-bootstrap/ng-bootstrap';
import { TranslationPipe } from '@pipes/translation/translation.pipe';
import { ActivityStateService } from '@services/activity/state/activity-state.service';
import { PersonService } from '@services/person/person.service';
import { SessionService } from '@services/session/session.service';
import { PersonCardComponent } from '@shared-components/person-card/person-card.component';
import {
  Observable,
  OperatorFunction,
  catchError,
  debounceTime,
  distinctUntilChanged,
  map,
  of,
  switchMap,
  tap
} from 'rxjs';
import { DataLoaderDirective, TdsTypeAheadComponent } from 'tangoe-design-system';

@Component({
  selector: 'ts-person-assignment',
  templateUrl: './person-assignment.component.html',
  imports: [
    CommonModule,
    TranslationPipe,
    DataLoaderDirective,
    TdsTypeAheadComponent,
    PersonCardComponent
  ]
})
export class PersonAssignmentComponent implements OnInit {
  @Input() shopping_cart: ShoppingCartData;
  @Input() is_review_or_summary: boolean;
  @Input() asset_details: ExistingAssetDetails;
  @Input() card_wrap: boolean = true;
  @Output() update_replacement_person: EventEmitter<{
    cart: ShoppingCartData;
    on_change?: boolean;
  }> = new EventEmitter();
  user_control: FormControl = new FormControl(null, [Validators.required]);
  input_format = (item: PersonItem) => item.display_value;
  user_not_found: boolean = false;
  loading: boolean = false;

  constructor(
    private activity_state: ActivityStateService,
    private person_svc: PersonService,
    private session_svc: SessionService
  ) {}

  ngOnInit(): void {
    if (this.shopping_cart) {
      this.user_control.setValue(this.shopping_cart.replacement_person, { emitEvent: false });
      this.user_control[this.shopping_cart.replacement_person ? 'disable' : 'enable']();
      this.update_replacement_person.emit({ cart: this.shopping_cart });
    }
  }

  get existing_user(): { name: string; email: string | undefined } {
    return {
      name: `${this.asset_details.existing_person?.first_name} ${this.asset_details.existing_person?.last_name}`,
      email: this.asset_details.existing_person?.email
    };
  }

  get replacement_user(): { name: string; email: string | undefined } {
    return {
      name: `${this.asset_details.replacement_user?.first_name} ${this.asset_details.replacement_user?.last_name}`,
      email: this.asset_details.replacement_user?.email
    };
  }

  getCurrentUser(): Partial<PersonItem> {
    return (
      this.activity_state.getOrderForPerson() ?? {
        id: this.session_svc.getUser()?.id,
        person_name: this.session_svc.getUserFullName(),
        email: this.session_svc.getEmail(true)
      }
    );
  }

  getCurrentUserLabel(): string {
    const user: Partial<PersonItem> = this.getCurrentUser();
    return user.person_name + (user.email ? ' (' + user.email + ')' : '');
  }

  selectUser(event?: NgbTypeaheadSelectItemEvent<PersonItem | undefined>): void {
    this.shopping_cart.replacement_person = event?.item;
    this.user_control.setValue(event?.item, { emitEvent: false });

    if (event?.item) this.user_control.disable();
    else this.user_control.enable();

    this.update_replacement_person.emit({ cart: this.shopping_cart, on_change: true });
  }

  search: OperatorFunction<string, readonly Partial<PersonItem>[]> = (term$: Observable<string>) =>
    term$.pipe(
      debounceTime(100),
      tap(() => (this.loading = this.user_not_found = false)),
      distinctUntilChanged(),
      switchMap((term: string) => {
        if (term.length < 3) return of([]);

        this.loading = true;
        return this.person_svc.search(term).pipe(
          map((res: PersonSearchOptions) => {
            this.loading = false;
            const user: Partial<PersonItem> = this.getCurrentUser();
            const users: PersonItem[] = res.items.reduce((acc: PersonItem[], item: PersonItem) => {
              if (item.id === user.id) return acc;
              return [
                ...acc,
                {
                  ...item,
                  display_value: `${item.person_name} (${item.email})`
                }
              ];
            }, []);

            this.user_not_found = users.length === 0;
            return users;
          }),
          catchError(() => {
            this.loading = this.user_not_found = false;
            return of([]);
          })
        );
      })
    );
}
